// Labour-rule checks for the rota. Pure functions, shared by the grid (as
// warnings on a shift and when picking someone) and the auto-draft. Rules
// only warn; nothing here blocks a plan. Times are local wall-clock times.
// See docs/plans/workday/staff_planning.md.

export type RuleSettings = {
  max_hours_day: number;
  max_hours_week: number;
  min_rest_hours: number;
  max_consecutive_days: number;
  publish_days_ahead: number;
};

export type PlannedShift = {
  id: number;
  shift_date: string; // YYYY-MM-DD
  start_time: string; // HH:MM
  end_time: string; // HH:MM; ≤ start means the next day
  break_minutes: number;
  role_id: number | null;
  staff_id: number | null;
  status?: string;
};

export type Person = { id: number; role_ids: number[]; contract_hours_week: number | null };

/** A date range a person is away (inclusive), e.g. approved time off. */
export type Absence = { staff_id: number; start_date: string; end_date: string };

export type WarningKind =
  | "overlap"
  | "rest"
  | "day_hours"
  | "week_hours"
  | "consecutive"
  | "skill"
  | "time_off"
  | "not_published";

export type RotaWarning = { kind: WarningKind; value?: number; limit?: number; date?: string };


const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/** Days since 1970 for a YYYY-MM-DD date (calendar arithmetic, no timezone). */
export const dayNumber = (iso: string) => Math.round(Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) / 86_400_000);

export const fromDayNumber = (n: number) => new Date(n * 86_400_000).toISOString().slice(0, 10);

/** Start and end as minutes since 1970 (wall clock), end after start. */
export function interval(s: Pick<PlannedShift, "shift_date" | "start_time" | "end_time">) {
  const base = dayNumber(s.shift_date) * 1440;
  const start = base + toMinutes(s.start_time);
  let end = base + toMinutes(s.end_time);
  if (end <= start) end += 1440;
  return { start, end };
}

/** Paid hours of a shift (length minus break). */
export const shiftHours = (s: PlannedShift) => {
  const { start, end } = interval(s);
  return Math.max(end - start - (s.break_minutes || 0), 0) / 60;
};

/** ISO week key: the day number of that week's Monday. */
export const weekKey = (iso: string) => {
  const n = dayNumber(iso);
  // 1970-01-01 was a Thursday (ISO 4).
  const isoDow = ((n + 3) % 7 + 7) % 7 + 1;
  return n - (isoDow - 1);
};

/**
 * What goes wrong if `person` works `shift`, given the rest of the plan.
 * `others` may include `shift` itself (it is ignored by id).
 */
export function warningsFor(
  shift: PlannedShift,
  person: Person,
  others: PlannedShift[],
  settings: RuleSettings,
  absences: Absence[] = [],
): RotaWarning[] {
  const mine = others.filter((o) => o.staff_id === person.id && o.id !== shift.id);
  const out: RotaWarning[] = [];
  const iv = interval(shift);

  if (shift.role_id != null && !person.role_ids.includes(shift.role_id)) out.push({ kind: "skill" });

  if (absences.some((a) => a.staff_id === person.id && shift.shift_date >= a.start_date && shift.shift_date <= a.end_date)) {
    out.push({ kind: "time_off" });
  }

  let closestGap = Infinity;
  for (const o of mine) {
    const oi = interval(o);
    if (oi.start < iv.end && iv.start < oi.end) {
      out.push({ kind: "overlap", date: o.shift_date });
      continue;
    }
    // Rest is between working days: a split shift (lunch, break, dinner)
    // on the same date doesn't count.
    if (o.shift_date === shift.shift_date) continue;
    const gap = oi.end <= iv.start ? iv.start - oi.end : oi.start - iv.end;
    closestGap = Math.min(closestGap, gap);
  }
  if (closestGap < settings.min_rest_hours * 60) {
    out.push({ kind: "rest", value: Math.round((closestGap / 60) * 10) / 10, limit: settings.min_rest_hours });
  }

  const dayHours = mine.filter((o) => o.shift_date === shift.shift_date).reduce((s, o) => s + shiftHours(o), shiftHours(shift));
  if (dayHours > settings.max_hours_day + 1e-9) out.push({ kind: "day_hours", value: round1(dayHours), limit: settings.max_hours_day });

  const wk = weekKey(shift.shift_date);
  const weekHours = mine.filter((o) => weekKey(o.shift_date) === wk).reduce((s, o) => s + shiftHours(o), shiftHours(shift));
  if (weekHours > settings.max_hours_week + 1e-9) out.push({ kind: "week_hours", value: round1(weekHours), limit: settings.max_hours_week });

  const days = new Set(mine.map((o) => dayNumber(o.shift_date)));
  days.add(dayNumber(shift.shift_date));
  const d = dayNumber(shift.shift_date);
  let run = 1;
  for (let x = d - 1; days.has(x); x--) run++;
  for (let x = d + 1; days.has(x); x++) run++;
  if (run > settings.max_consecutive_days) out.push({ kind: "consecutive", value: run, limit: settings.max_consecutive_days });

  return out;
}

/** Warnings for every assigned shift in a plan, by shift id. */
export function planWarnings(
  shifts: PlannedShift[],
  people: Person[],
  settings: RuleSettings,
  absences: Absence[] = [],
  today?: string,
): Map<number, RotaWarning[]> {
  const byId = new Map(people.map((p) => [p.id, p]));
  const out = new Map<number, RotaWarning[]>();
  for (const s of shifts) {
    const list: RotaWarning[] = [];
    const person = s.staff_id != null ? byId.get(s.staff_id) : undefined;
    if (person) list.push(...warningsFor(s, person, shifts, settings, absences));
    if (today && s.status === "draft" && dayNumber(s.shift_date) - dayNumber(today) < settings.publish_days_ahead) {
      list.push({ kind: "not_published", limit: settings.publish_days_ahead });
    }
    if (list.length) out.set(s.id, list);
  }
  return out;
}

/** Hours each person is planned for in the ISO week of `anyDayInWeek`. */
export function weekHoursByPerson(shifts: PlannedShift[], anyDayInWeek: string) {
  const wk = weekKey(anyDayInWeek);
  const out = new Map<number, number>();
  for (const s of shifts) {
    if (s.staff_id == null || weekKey(s.shift_date) !== wk) continue;
    out.set(s.staff_id, (out.get(s.staff_id) ?? 0) + shiftHours(s));
  }
  return out;
}

const round1 = (n: number) => Math.round(n * 10) / 10;

